import { useEffect, useState } from 'react';
import { ROME, WeatherLocation } from '../components/admin/v2/DashboardWelcome';

const STORAGE_KEY = 'unasca.geo.city';
const MAX_ENTRIES = 20;
type Stored = Record<string, WeatherLocation>;

export const cityKey = (citta?: string, provincia?: string) => `${(citta || '').trim().toLowerCase()}|${(provincia || '').trim().toUpperCase()}`;

function readStore(): Stored {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}') || {}; } catch { return {}; }
}
function writeStore(key: string, value: WeatherLocation) {
  try {
    const entries = Object.entries(readStore()).filter(([k]) => k !== key).slice(-(MAX_ENTRIES - 1));
    localStorage.setItem(STORAGE_KEY, JSON.stringify(Object.fromEntries([...entries, [key, value]])));
  } catch { /* storage non disponibile: si rigeocodifica al prossimo accesso */ }
}

/** Sceglie il risultato Open-Meteo: primo con la stessa provincia (admin2/admin3 contengono la sigla o il nome), altrimenti il primo. */
export function pickResult(results: any[], citta: string, provincia?: string): WeatherLocation | null {
  const list = (results || []).filter(r => Number.isFinite(r?.latitude) && Number.isFinite(r?.longitude));
  if (!list.length) return null;
  const sameName = list.filter(r => typeof r.name === 'string' && r.name.toLowerCase() === citta.trim().toLowerCase());
  const pool = sameName.length ? sameName : list;
  const prov = (provincia || '').trim().toLowerCase();
  const match = prov ? pool.find(r => [r.admin2, r.admin3].some(a => typeof a === 'string' && a.toLowerCase().includes(prov))) : undefined;
  const r = match || pool[0];
  return { latitude: r.latitude, longitude: r.longitude, label: r.name || citta.trim() };
}

/** Coordinate di una città italiana (geocoding Open-Meteo, cache locale). Senza città o in caso di errore: Roma. */
export function useCityCoordinates(citta?: string, provincia?: string): WeatherLocation {
  const key = cityKey(citta, provincia);
  const [loc, setLoc] = useState<WeatherLocation>(() => (citta?.trim() && readStore()[key]) || ROME);
  useEffect(() => {
    const name = citta?.trim();
    if (!name) { setLoc(ROME); return; }
    const stored = readStore()[key];
    if (stored) { setLoc(stored); return; }
    const controller = new AbortController();
    (async () => {
      try {
        const response = await fetch(`https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(name)}&count=10&language=it&countryCode=IT&format=json`, { signal: controller.signal, credentials: 'omit' });
        if (!response.ok) throw new Error('Geocoding unavailable');
        const picked = pickResult((await response.json()).results, name, provincia);
        if (!picked) throw new Error('Not found');
        writeStore(key, picked);
        setLoc(picked);
      } catch { if (!controller.signal.aborted) setLoc(ROME); }
    })();
    return () => controller.abort();
  }, [key, citta, provincia]);
  return loc;
}
